// sections/SectionAction.jsx
import React, { useState } from 'react';
import { CheckCircle, ArrowRightCircle } from 'lucide-react';
import { Row, Col, Button, SectionTitle } from '../../../SharedInputs.jsx';
import {
  updateStudentOfferLetter
} from '../../../../services/StudentRecordService.js';
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { CODE_TO_COUNTRY } from '../../../../CountrySACC.js';
import { fileStorageService } from "../../../../services/FileStorageService.js";
import ImageModule from "docxtemplater-image-module-free";
import {getFirstCourseByName} from "../../../../services/CourseService.js";
import { SignatureInput, FormField, Input } from '../../../SharedInputs.jsx';

export function SectionAction({ initialData: initialData, onCancel, setParentInitialData }) {
   // keep local copy of initial data to reflect changes immediately
  //   const [initialData, setParentInitialData] = useState(initial || {});
  // Your sample data structure - replace with actual data
  const accepted = initialData?.status === "accepted";
  const pending = initialData?.status === "pending";
  const shifted = initialData?.migratedToStudentSystem || false;
  // Correctly declare state
  const response = initialData?.axcelerateResponse || { type: "", message: "" };
  const templatePath = "/templates/OfferLetter-1.docx";
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const [inputData, setInputData] = useState(initialData?.formState.offerLetterDetails || {
    theoryLocation: "",
    practicalLocation: "",
    admissionOfferSign: "",
    orientationDate: "",
    orientationTime: "",
    orientationLocation: "",
  });


  const processDocxTemplate = (arrayBuffer, mapping) => {
    const zip = new PizZip(arrayBuffer);
    // Image module configuration
    const imageOpts = {
      centered: false, // Set to true if you want centered images
      getImage: (tagValue) => {
        // Convert base64 to array buffer
        if (tagValue && tagValue.startsWith('data:image')) {
          // Remove data:image/png;base64, prefix
          const base64Data = tagValue.split(',')[1];
          const binaryString = atob(base64Data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        }
        // If just base64 without prefix
        const binaryString = atob(tagValue);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      },
      getSize: (img, tagValue, tagName) => {
        return [250, 100]; // Default size
      }
    };
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: "[[", end: "]]" },  // <-- must match template
      modules: [new ImageModule(imageOpts)]
    });

    try {
      doc.render(mapping);
    } catch (error) {
      throw error;
    }

    const out = doc.getZip().generate({ type: "blob" });
    return out;
  };

  const createTemplateMapping = async (data) => {
    let courseTitleName = "";
    let cricosCodeName = "";
    const getCountryCode = (countryCode) => CODE_TO_COUNTRY[countryCode] || "";

    // Exclude a course only when BOTH its start date and end date are in the past.
    // Missing/invalid dates are kept so we do not accidentally remove a course.
    const isPastCompletedCourse = (course) => {
      if (!course?.courseStartDate || !course?.courseEndDate) return false;

      const startDate = new Date(course.courseStartDate);
      const endDate = new Date(course.courseEndDate);

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return startDate < today && endDate < today;
    };

    const isPackageMode = data?.feePlanType === "Package";

    // Independent mode: remove completed/past courses before building
    // course details, course title/CRICOS and their fee plans.
    const itemsList = isPackageMode
      ? data.packages || []
      : (data.courses || []).filter((course) => !isPastCompletedCourse(course) &&
        !course?.coeNumber?.trim());

    const coursesList = (
      await Promise.all(
        itemsList.map(async (item) => {
          if (!isPackageMode) {
            // 🔹 SINGLE COURSE MODE
            const courseData = await getFirstCourseByName(item.courseName);
            courseTitleName +=
              (courseTitleName ? " + " : "") +
              `${courseData?.courseCode || ""} ${courseData?.courseName || ""}`;
            cricosCodeName +=
              (cricosCodeName ? " + " : "") + (courseData?.cricosCode || "");

            let counter = 0;
            const fees = item?.feesPlan || {};
            const leftOverFees =
              (fees?.initialDeposit?.depositAmount || 0) -
              (fees?.enrollmentFees || 0) -
              (fees?.materialFees || 0);

            const feeInstallments = (fees?.installments || []).map((inst) => ({
              feeNo: ++counter,
              feeInstalment: inst.depositAmount ? `$${inst.depositAmount}` : "",
              dueDate: formatDateDDMMYYYY(inst.dueDate) || "",
            }));

            const feeBreakDown = {
              courseName:
                ": " +
                (courseData?.courseCode || "") +
                " " +
                (courseData?.courseName || ""),
              cEnrollFees: fees?.enrollmentFees ? `$${fees.enrollmentFees}` : "",
              cTuitionFees: fees?.tuitionFees ? `$${fees.tuitionFees}` : "",
              cMaterialFees: fees?.materialFees ? `$${fees.materialFees}` : "",
              cOSHCFees: fees?.oshcFees ? `$${fees.oshcFees}` : "N/A",
              cAdditionalFees: fees?.additionalFees
                ? `$${fees.additionalFees}`
                : "N/A",
              cleftOver: leftOverFees > 0 ? ` + $${leftOverFees} tuition fees` : "",
              ctotalFees: fees?.totalFees ? `$${fees.totalFees}` : "",
              initialDeposit: fees?.initialDeposit?.depositAmount
                ? `$${fees.initialDeposit.depositAmount}`
                : "",
              feeInstalment: feeInstallments || [],
            };

            return {
              courseName:
                ": " +
                (courseData?.courseCode || "") +
                " " +
                (courseData?.courseName || ""),
              courseStartDate: formatDateDDMMYYYY(item.courseStartDate) || "",
              courseEndDate: formatDateDDMMYYYY(item.courseEndDate) || "",
              courseTotalDuration: item.courseDuration
                ? `${item.courseDuration} weeks`
                : "",
              tuitionWeeks: item.courseTuitionWeeks
                ? `${item.courseTuitionWeeks} weeks`
                : "",
              holidayBreaks: item.courseHolidayWeeks
                ? `${item.courseHolidayWeeks} weeks`
                : "",
              hoursPerWeek: "20 Hours",
              feeBreakdown: feeBreakDown,
            };
          } else {
            // PACKAGE MODE
            const pkg = item;
            const packageFees = pkg.feesPlan || {};
            let counter = 0;

            // --- Calculate leftover deposit ---
            const leftOverFees =
              (Number(packageFees?.initialDeposit?.depositAmount) || 0) -
              (Number(packageFees?.enrollmentFees) || 0) -
              (Number(packageFees?.materialFees) || 0);

            // --- Build installment list ---
            const feeInstallments = (packageFees?.installments || []).map((inst) => ({
              feeNo: ++counter,
              feeInstalment: inst.depositAmount ? `$${inst.depositAmount}` : "",
              dueDate: formatDateDDMMYYYY(inst.dueDate) || "",
            }));

            // --- Build individual course list ---
            // Remove courses where BOTH start and end dates are already in the past.
            // If all courses in this package are past, courseBreakdown will be empty
            // and the package fee plan will not be added to the Offer Letter.
            const courseEntries = Object.values(pkg.courses || {}).filter(
              (course) => !isPastCompletedCourse(course) &&
                  !course?.coeNumber?.trim()
            );
            const totalCourses = courseEntries.length;

            const courseBreakdown = await Promise.all(
              courseEntries.map(async (course, index) => {
                const courseData = await getFirstCourseByName(course.courseName);
                 // --- Update title + CRICOS codes ---
                courseTitleName +=
                  (courseTitleName ? " + " : "") +
                  (courseData
                    ? `${courseData?.courseCode || ""} ${courseData?.courseName || ""}`
                    : "");
                cricosCodeName +=
                  (cricosCodeName ? " + " : "") +
                  courseData?.cricosCode || "";
                const baseDetails = {
                  courseName:
                    ": " +
                    (courseData?.courseCode || "") +
                    " " +
                    (course.courseName || ""),
                  courseStartDate: formatDateDDMMYYYY(course.courseStartDate) || "",
                  courseEndDate: formatDateDDMMYYYY(course.courseEndDate) || "",
                  courseTotalDuration: course.courseDuration
                    ? `${course.courseDuration} weeks`
                    : "",
                  tuitionWeeks: course.courseTuitionWeeks
                    ? `${course.courseTuitionWeeks} weeks`
                    : "",
                  holidayBreaks: course.courseHolidayWeeks
                    ? `${course.courseHolidayWeeks} weeks`
                    : "",
                  hoursPerWeek: "20 Hours",
                };

                // Attach package fees only to the LAST INCLUDED course.
                // Past/completed courses have already been removed above.
                if (index === totalCourses - 1) {
                  baseDetails.feeBreakdown = {
                    packageName: pkg.name,
                    cEnrollFees: packageFees?.enrollmentFees
                      ? `$${packageFees.enrollmentFees}`
                      : "",
                    cTuitionFees: packageFees?.tuitionFees
                      ? `$${packageFees.tuitionFees}`
                      : "",
                    cMaterialFees: packageFees?.materialFees
                      ? `$${packageFees.materialFees}`
                      : "",
                    cOSHCFees: packageFees?.oshcFees ? `$${packageFees.oshcFees}` : "N/A",
                    cAdditionalFees: packageFees?.additionalFees
                      ? `$${packageFees.additionalFees}`
                      : "N/A",
                    cleftOver: leftOverFees > 0 ? ` + $${leftOverFees} tuition fees` : "",
                    ctotalFees: packageFees?.totalFees ? `$${packageFees.totalFees}` : "",
                    initialDeposit: packageFees?.initialDeposit?.depositAmount
                      ? `$${packageFees.initialDeposit.depositAmount}`
                      : "",
                    feeInstalment: feeInstallments || [],
                  };
                }

                return baseDetails;
              })
            );

            // --- Return flat list of courses (last one includes package fees) ---
            return courseBreakdown;
          }
        })
      )
    ).flat(); // 🔸 flatten packages into single list of course objects

    // --- Final mapping object ---
    return {
      offerDate: formatDateDDMMYYYY(new Date()),
      orientationDate: formatDateDDMMYYYY(inputData.orientationDate) || "",
      orientationTime: formatTimeToAMPM(inputData.orientationTime) || "",
      orientationLocation: inputData.orientationLocation || "",
      firstName: data.firstName || "",
      surname: data.lastName || "",
      dateOfBirth: formatDateDDMMYYYY(data.dateOfBirth) || "",
      gender: data.gender || "",
      // These values now come from the STUDENT RECORD formState.
      // The student formState remains flat; no existing data structure is changed.
      studentNationality:
        getCountryCode(data.citizenship) || data.citizenship || "",

      // Full address stays exactly as entered in the Student Record.
      studentAddress: data.address || "",

      // These separate fields populate CRES / POSTCODE / STATE in the Offer Letter.
      cres: data.suburb || "",
      postcode: data.postcode || "",
      state: data.state || "",

      passportNo: data.passportNumber || "",
      passportExp: formatDateDDMMYYYY(data.passportExpiry) || "",
      studentPhone: data.phone || "",
      studentEmail: data.email || "",

      agentName: data.agentName || "",
      visaApplicationType: data.visaApplicationType || "",

      visaType: data.visaType || "",
      expiryDate:
        formatDateDDMMYYYY(data.visaExpiryDate) || "",

      campusName: data.campusName || "",
      admissionOfficer: inputData.admissionOfferSign || "Electronically signed",
      courseTitle: courseTitleName,
      cricosCode: cricosCodeName,
      theoryLocation:
        inputData.theoryLocation !== ""
          ? "Theory: " + inputData.theoryLocation
          : "",
      practicalLocation:
        inputData.practicalLocation !== ""
          ? "Practical: " + inputData.practicalLocation
          : "",
      courses: coursesList || [],
    };
  };




  const formatTimeToAMPM = (time24) => {
    if (!time24) return '';
    const [hour, minute] = time24.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };
  const formatDateDDMMYYYY = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };


  // Generate Offer Letter → DOCX
  const handleOfferLetter = async () => {
    if (!initialData?.id) return;
    
    if (!inputData.orientationDate || !inputData.orientationLocation || !inputData.orientationTime || !inputData.admissionOfferSign || !inputData.theoryLocation) {
      alert("Fill the mandatory fields")
      return;
    }

    try {
      setIsLoading(true);
      setProgress(10); 
      // 1. Fetch template
      const response = await fetch(templatePath);
      if (!response.ok) throw new Error("Template not found");
      
      setProgress(30);
      const arrayBuffer = await response.arrayBuffer();
      const templateMapping = await createTemplateMapping(initialData.formState);
      setProgress(50);

      // 2. Fill DOCX template
      const filledDoc = processDocxTemplate(arrayBuffer, templateMapping);
      setProgress(70);


      const filePath = `student/offerLetter/offerletter-${initialData.id}.docx`;

      // 3. Check if file exists
      let fileUrl;
      try {
        fileUrl = await fileStorageService.getFileUrl(filePath);
      } catch (err) {
      }
      setProgress(80);

      // 4. Upload (this will overwrite if file exists)
      const fileData = await fileStorageService.uploadFile(filledDoc, filePath);
      setProgress(90);

      // Keep offerLetterDetails in the EXISTING Student Record formState structure.
      const updatedStudentData = {
        ...initialData,
        formState: {
          ...(initialData.formState || {}),
          offerLetterDetails: inputData,
        },
      };

      const offerLetterApplication = {
        url: fileData.url,
        path: fileData.path,
        name: `offerletter-${initialData.id}.docx`
      };

      // 5. Update Student Record only.
      await updateStudentOfferLetter(
        initialData.id,
        updatedStudentData,
        offerLetterApplication
      );

      if (typeof setParentInitialData === "function") {
        setParentInitialData({
          ...updatedStudentData,
          offerLetterApplication,
        });
      }
      setProgress(100);

      // 6. Open uploaded file in a new tab
      window.open(fileData.url, "_blank");

    } catch (err) {
      console.log("ERROR", err.message);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };
  return (
    <div>
      <SectionTitle icon={CheckCircle}>Actions</SectionTitle>
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          background: "#fafafa",
        }}
      >
        <h4 style={{ marginBottom: 12 }}>ORIENTATION DETAILS</h4>

        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <FormField label="Orientation Date" required>
              <Input
                type="date"
                placeholder="Enter orientation date"
                value={inputData.orientationDate}
                onChange={(e) =>
                  setInputData({ ...inputData, orientationDate: e.target.value })
                }
              />
            </FormField>
          </div>

          <div style={{ flex: 1 }}>
            <FormField label="Orientation Time" required>
              <Input
                type="time"
                placeholder="Enter orientation time"
                value={inputData.orientationTime}
                onChange={(e) =>
                  setInputData({ ...inputData, orientationTime: e.target.value })
                }
              />
            </FormField>
          </div>

          <div style={{ flex: 1 }}>
            <FormField label="Orientation Location" required>
              <Input
                placeholder="Enter orientation location"
                value={inputData.orientationLocation}
                onChange={(e) =>
                  setInputData({ ...inputData, orientationLocation: e.target.value })
                }
              />
            </FormField>
          </div>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          background: "#fafafa",
        }}
      >
        <h4 style={{ marginBottom: 12 }}>COURSE STUDY LOCATION</h4>

        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <FormField label="Theory Location" required>
              <Input
                placeholder="Enter theory location"
                value={inputData.theoryLocation}
                onChange={(e) =>
                  setInputData({ ...inputData, theoryLocation: e.target.value })
                }
              />
            </FormField>
          </div>

          <div style={{ flex: 1 }}>
            <FormField label="Practical Location">
              <Input
                placeholder="Enter practical location"
                value={inputData.practicalLocation}
                onChange={(e) =>
                  setInputData({ ...inputData, practicalLocation: e.target.value })
                }
              />
            </FormField>
          </div>
        </div>
      </div>


      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <FormField label="Admission Officer Signature" style={{height: '100px'}} required>
            <SignatureInput
              value={inputData.admissionOfferSign}
              onChange={(sig) =>
                setInputData({ ...inputData, admissionOfferSign: sig })
              }
            />
          </FormField>
        </div>
      </div>
      {isLoading && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ width: '100%', height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: '#4dd0e1',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <p style={{ fontSize: 12, marginTop: 4 }}>Generating offer letter... {progress}%</p>
        </div>
      )}


      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={ArrowRightCircle}
            onClick={handleOfferLetter}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: isLoading ? '#b2ebf2' : '#4dd0e1',
              color: '#006064',
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Generating...' : 'Generate Offer Letter'}
          </Button>
          <p>
            {initialData?.offerLetterApplication?.url ? <a href={initialData?.offerLetterApplication?.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              View Offer Letter
            </a> : null}
          </p>
        </Col>


      </Row>
    </div>
  );
}